import { useEffect, useMemo, useRef, useState } from 'react'
import { DateTime } from 'luxon'
import ReactDatePicker, {
  DatePickerProps,
  ReactDatePickerCustomHeaderProps,
} from 'react-datepicker'
import styled from 'styled-components'
import { omit } from 'lodash'
import {
  faAngleLeft,
  faAngleRight,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons'
import { faCaretUp, faCaretDown } from '@fortawesome/pro-solid-svg-icons'
import { shift } from '@floating-ui/dom'

import 'react-datepicker/dist/react-datepicker.css'

import { Colors, Fonts, FontWeight } from '../../styles/theme'
import Input, { InputProps } from './Input'

import { DATE_FORMATS_LUXON } from '../../utils/dateHelpers'
import {
  QUARTER_PNL_OPTIONS,
  YEARLY_PNL_OPTIONS,
} from '../../constants/businessConstants'
import {
  DeviceWidth,
  DeviceWidthCombo,
  useIsDeviceWidth,
} from '../../utils/deviceWidthHelpers'
import Modal from './Modal'
import Icon, { IconButton } from './Icon'
import Text from './Text'
import Button from './Button'
import Dropdown from './Dropdown'

type RangeProps =
  | {
      selectsRange?: false
      onChange: (value: string) => void
    }
  | {
      selectsRange: true
      onChange: (value: [string, string]) => void
    }

interface BaseProps
  extends
    Omit<
      DatePickerProps,
      | 'value'
      | 'onChange'
      | 'dateFormat'
      | 'onFocus'
      | 'startDate'
      | 'endDate'
      | 'selectsRange'
      | 'autoComplete'
      | 'onBlur'
      | 'onSelect'
      | 'excludeScrollbar'
      | 'showMonthYearDropdown'
      | 'formatMultipleDates'
    >,
    Omit<
      InputProps,
      | 'onChange'
      | 'onKeyDown'
      | 'tabIndex'
      | 'onSelect'
      | 'customInput'
      | 'aria-invalid'
      | 'aria-required'
    > {
  dateFormat?: DATE_FORMATS_LUXON
  startDate?: string
  endDate?: string
}

export const parseDateStringToDate = (
  dateString: string | undefined
): Date | undefined => {
  if (!dateString) return undefined
  const iso = DateTime.fromISO(dateString)
  if (iso.isValid) return iso.toJSDate()
  const formats = [
    DATE_FORMATS_LUXON.INPUT,
    DATE_FORMATS_LUXON.DISPLAY_SIMPLE,
    DATE_FORMATS_LUXON.DISPLAY_SHORT,
    DATE_FORMATS_LUXON.DISPLAY_LONG,
    DATE_FORMATS_LUXON.GUSTO_SUBMIT,
    DATE_FORMATS_LUXON.YEAR_MONTH,
  ]
  for (const fmt of formats) {
    const dt = DateTime.fromFormat(dateString, fmt)
    if (dt.isValid) return dt.toJSDate()
  }
  const native = new Date(dateString)
  return isNaN(native.getTime()) ? undefined : native
}

export const convertSelectedDateToString = (
  date: Date,
  format: DATE_FORMATS_LUXON
): string => DateTime.fromJSDate(date).startOf('day').toFormat(format)

export const formatPartialDateStringToDateTime = (
  value: string,
  format: DATE_FORMATS_LUXON
): DateTime | null => {
  if (!value) return null
  if (
    format === DATE_FORMATS_LUXON.INPUT ||
    format === DATE_FORMATS_LUXON.DISPLAY_SIMPLE
  ) {
    const sep = format === DATE_FORMATS_LUXON.INPUT ? '-' : '/'
    const [m, d, y] = value.split(sep)
    let parseFormat: string | null = null
    let parseValue = value
    if (m !== undefined && !d) {
      parseFormat = 'L'
      parseValue = m
    } else if (d !== undefined && !y) {
      parseFormat = `L${sep}d`
      parseValue = `${m}${sep}${d}`
    } else if (y?.length === 4) {
      parseFormat = `L${sep}d${sep}yyyy`
      parseValue = value
    } else if (y !== undefined) {
      parseFormat = `L${sep}d`
      parseValue = `${m}${sep}${d}`
    }
    if (parseFormat) {
      const dt = DateTime.fromFormat(parseValue, parseFormat)
      if (dt.isValid) return dt
    }
  }
  const parsed = DateTime.fromISO(value)
  return parsed.isValid ? parsed : null
}

type Props = BaseProps & RangeProps

const DatePickerInput = ({
  onChange,
  selectsRange,
  dateFormat,
  ...rest
}: Props & { onClick?: () => void; dateFormat: DATE_FORMATS_LUXON }) => (
  <Input
    {...omit(
      rest,
      'customInput',
      'onSelect',
      'startDate',
      'endDate',
      'onCalendarOpen',
      'onCalendarClose',
      'maxDate',
      'aria-invalid',
      'aria-required'
    )}
    className="dateInput"
    leftIcon="calendar outline"
    componentType="date"
    onChange={(value: string) => {
      if (!selectsRange) {
        onChange(value)
      }
    }}
    onBlur={(e) => {
      rest.onBlur?.(e)
      const parsedDate = parseDateStringToDate(e.target.value)
      if (parsedDate && !selectsRange) {
        onChange(DateTime.fromJSDate(parsedDate).toFormat(dateFormat))
      }
    }}
  />
)

const CustomDateHeader = ({
  showYear,
  toggleShowYear,
  date,
  decreaseYear,
  decreaseMonth,
  increaseYear,
  increaseMonth,
  showYearPicker,
}: ReactDatePickerCustomHeaderProps & {
  showYear: boolean
  showYearPicker?: boolean
  toggleShowYear: () => void
}) => (
  <div style={{ display: 'flex', marginBottom: 20 }}>
    <div
      onClick={() => !showYearPicker && toggleShowYear()}
      style={{ display: 'flex', flex: 1, marginLeft: 25, cursor: 'pointer' }}
      role="presentation"
    >
      <Text as="h2">
        {showYear
          ? 'Select Year'
          : DateTime.fromJSDate(date).toFormat(
              DATE_FORMATS_LUXON.MONTH_YEAR_LONG
            )}
      </Text>
      {!showYearPicker && (
        <Icon
          icon={showYear ? faCaretUp : faCaretDown}
          style={{ marginLeft: 10, color: Colors.darkGray }}
        />
      )}
    </div>

    <IconButton
      icon={faAngleLeft}
      onClick={showYear ? decreaseYear : decreaseMonth}
      style={{ marginRight: 30 }}
    />
    <IconButton
      icon={faAngleRight}
      onClick={showYear ? increaseYear : increaseMonth}
      style={{ marginRight: 35 }}
    />
  </div>
)

const DatePicker = (props: Props) => {
  const {
    dateFormat = DATE_FORMATS_LUXON.INPUT,
    onChange,
    value,
    className,
    startDate,
    endDate,
    selectsRange,
    showYearPicker,
  } = props
  const pickerRef = useRef<ReactDatePicker>(null)
  const fallbackRange = useMemo(
    () => [parseDateStringToDate(startDate), parseDateStringToDate(endDate)],
    [startDate, endDate]
  )
  const isMobileOrTablet = useIsDeviceWidth(DeviceWidthCombo.mobileOrTablet)
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const [openModal, setOpenModal] = useState(false)
  const [showYear, setShowYear] = useState(Boolean(showYearPicker))

  // Tells the popover to stay within the content boundaries and give it a little extra padding
  const popoverModifiers = useMemo(
    () => [
      shift({
        boundary: document.querySelector('.content-wrapper') || undefined,
        padding: 10,
      }),
    ],
    []
  )

  useEffect(() => {
    if (isMobile) {
      pickerRef.current?.setOpen(openModal)
    }
  }, [isMobile, openModal])

  const [[start, end], setRange] = useState(fallbackRange)

  // This is not a perfect representation of the value sent back to parent - it's for the date picker selection so
  // doesn't have to be perfect
  const selectedDate = useMemo(() => {
    if (!value) {
      return null
    }

    const parsedDt = formatPartialDateStringToDateTime(value, dateFormat)

    return parsedDt?.isValid
      ? new Date(parsedDt.year, parsedDt.month - 1, parsedDt.day)
      : null
  }, [dateFormat, value])

  // This is not a perfect representation of the value sent back to parent - it's for the date picker selection so
  // doesn't have to be perfect
  const dateValue = useMemo(() => {
    const defaultDate = new Date().toISOString()
    if (!value) {
      return defaultDate
    }

    const parsedDt = formatPartialDateStringToDateTime(value, dateFormat)

    return parsedDt?.isValid
      ? new Date(parsedDt.year, parsedDt.month - 1, parsedDt.day).toISOString()
      : defaultDate
  }, [dateFormat, value])

  const selectedYearlyOption = useMemo(() => {
    const index = YEARLY_PNL_OPTIONS.findIndex(
      (option) => option.startDate === startDate && option.endDate === endDate
    )
    return index === -1 ? undefined : index
  }, [endDate, startDate])

  const selectedQuarterOption = useMemo(() => {
    const index = QUARTER_PNL_OPTIONS.findIndex(
      (option) => option.startDate === startDate && option.endDate === endDate
    )
    return index === -1 ? undefined : index
  }, [endDate, startDate])

  const handleClearClick = () => {
    if (selectsRange) {
      onChange(['', ''])
      setRange([undefined, undefined])
    } else {
      onChange('')
    }
  }

  const handleQuickOption = ({
    startDate: start,
    endDate: end,
  }: {
    startDate: string
    endDate: string
  }) => {
    setRange([parseDateStringToDate(start), parseDateStringToDate(end)])
    if (selectsRange) {
      onChange([start, end])
    }
    pickerRef.current?.setOpen(false)
  }

  if (isMobile && selectsRange) {
    return (
      <div>
        <DatePickerInput
          {...props}
          dateFormat={dateFormat}
          onClearClick={handleClearClick}
          onClick={() => setOpenModal(true)}
        />
        <Modal
          className={`${className} mobile-date-picker`}
          size="fullscreen"
          open={openModal}
        >
          <Modal.Content>
            <ReactDatePicker
              {...props}
              selectsRange
              selectsMultiple={undefined}
              dateFormat={dateFormat}
              disabledKeyboardNavigation
              endDate={end}
              focusSelectedMonth
              monthsShown={1}
              onChange={(dateOrRange) => {
                if (Array.isArray(dateOrRange)) {
                  setRange([
                    dateOrRange[0] || undefined,
                    dateOrRange[1] || undefined,
                  ])
                }
              }}
              ref={pickerRef}
              open={openModal}
              selected={selectedDate}
              shouldCloseOnSelect={!selectsRange || Boolean(showYearPicker)}
              startDate={start}
              value={dateValue}
              useWeekdaysShort
              popperModifiers={popoverModifiers}
              isClearable
            >
              {selectsRange && (
                <>
                  <div className="left-panel">
                    {isMobile && (
                      <div>
                        <Icon
                          icon={faXmark}
                          onClick={() => {
                            setOpenModal(false)
                          }}
                        />
                      </div>
                    )}
                    <div>
                      <Text
                        as="eyebrow"
                        style={{ marginBottom: 8 }}
                        color="forest"
                      >
                        By Year
                      </Text>
                      <Dropdown
                        placeholder="Select"
                        options={YEARLY_PNL_OPTIONS.map((option, index) => ({
                          text: option.value,
                          value: index,
                        }))}
                        onChange={(index) =>
                          handleQuickOption(YEARLY_PNL_OPTIONS[index])
                        }
                        value={selectedYearlyOption}
                        fullWidth
                      />
                    </div>
                    <div>
                      <Text
                        as="eyebrow"
                        style={{
                          marginBottom: 8,
                          marginTop: 0,
                        }}
                        color="forest"
                      >
                        By Quarter
                      </Text>
                      <Dropdown
                        placeholder="Select"
                        options={QUARTER_PNL_OPTIONS.map((option, index) => ({
                          text: option.value,
                          value: index,
                        }))}
                        onChange={(index) =>
                          handleQuickOption(QUARTER_PNL_OPTIONS[index])
                        }
                        value={selectedQuarterOption}
                        fullWidth
                      />
                    </div>
                  </div>
                  <div className="datepicker-ctas">
                    <Button
                      className="cancel"
                      onClick={() => {
                        setRange(fallbackRange)
                        setOpenModal(false)
                      }}
                      size="large"
                      variant="actionLink"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => {
                        onChange([
                          start
                            ? convertSelectedDateToString(start, dateFormat)
                            : '',
                          end
                            ? convertSelectedDateToString(end, dateFormat)
                            : '',
                        ])
                        setOpenModal(false)
                      }}
                      size="large"
                    >
                      Apply
                    </Button>
                  </div>
                </>
              )}
            </ReactDatePicker>
          </Modal.Content>
        </Modal>
      </div>
    )
  } else {
    const rangeProps = selectsRange
      ? {
          selectsRange: true as const,
          onChange: (dateOrRange: [Date | null, Date | null]) => {
            if (Array.isArray(dateOrRange)) {
              setRange([
                dateOrRange[0] || undefined,
                dateOrRange[1] || undefined,
              ])
            }
          },
          selectsMultiple: undefined,
        }
      : {
          selectsRange: undefined,
          onChange: (dateOrRange: Date | null) => {
            if (!Array.isArray(dateOrRange) && dateOrRange) {
              onChange(convertSelectedDateToString(dateOrRange, dateFormat))
            }
          },
          selectsMultiple: undefined,
        }

    return (
      <div
        className={`${className} ${
          isMobileOrTablet ? 'tablet-date-picker' : ''
        }`}
      >
        <DatePickerInput
          {...props}
          dateFormat={dateFormat}
          onClearClick={handleClearClick}
          onClick={() => pickerRef.current?.setOpen(true)}
        />
        <ReactDatePicker
          {...props}
          {...rangeProps}
          dateFormat={dateFormat}
          disabledKeyboardNavigation
          endDate={end}
          focusSelectedMonth
          monthsShown={selectsRange && !isMobileOrTablet ? 2 : 1}
          ref={pickerRef}
          selected={selectedDate}
          shouldCloseOnSelect={
            (!selectsRange && !showYear) || Boolean(showYearPicker)
          }
          startDate={start}
          value={dateValue}
          useWeekdaysShort
          renderCustomHeader={
            !selectsRange
              ? ({ ...props }) => (
                  <CustomDateHeader
                    {...props}
                    showYear={showYear}
                    showYearPicker={showYearPicker}
                    toggleShowYear={() => {
                      setShowYear((prev) => !prev)
                      // If you change the year pages the month pages also change even if a year is not selected and
                      // there's also no way to set the open date.  This is a hack to set the month back to whatever is selected
                      // It does end up flashing but it's better than the other behavior
                      pickerRef.current?.setOpen(false, true)
                      // There is a delay here because if the modal is set to closed -> open without this the modal doesn't actually close
                      setTimeout(
                        () => pickerRef.current?.setOpen(true, true),
                        0
                      )
                    }}
                  />
                )
              : undefined
          }
          onSelect={() => setShowYear(Boolean(showYearPicker))}
          showYearPicker={showYear}
          popperModifiers={popoverModifiers}
          isClearable
        >
          {selectsRange && (
            <>
              <div className="left-panel">
                <div>
                  <Text as="eyebrow" style={{ marginBottom: 8 }} color="forest">
                    By Year
                  </Text>
                  <Dropdown
                    placeholder={isMobileOrTablet ? 'Select' : 'Select year'}
                    options={YEARLY_PNL_OPTIONS.map((option, index) => ({
                      text: option.value,
                      value: index,
                    }))}
                    onChange={(index) =>
                      handleQuickOption(YEARLY_PNL_OPTIONS[index])
                    }
                    value={selectedYearlyOption}
                    fullWidth
                  />
                </div>
                <div>
                  <Text
                    as="eyebrow"
                    style={{
                      marginBottom: 8,
                      marginTop: isMobileOrTablet ? 0 : 40,
                    }}
                    color="forest"
                  >
                    By Quarter
                  </Text>
                  <Dropdown
                    placeholder={isMobileOrTablet ? 'Select' : 'Select quarter'}
                    options={QUARTER_PNL_OPTIONS.map((option, index) => ({
                      text: option.value,
                      value: index,
                    }))}
                    onChange={(index) =>
                      handleQuickOption(QUARTER_PNL_OPTIONS[index])
                    }
                    value={selectedQuarterOption}
                    fullWidth
                  />
                </div>
              </div>
              <div className="datepicker-ctas">
                <Button
                  className="cancel"
                  onClick={() => {
                    setRange(fallbackRange)
                    pickerRef.current?.setOpen(false)
                  }}
                  size="large"
                  variant="actionLink"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    onChange([
                      start
                        ? convertSelectedDateToString(start, dateFormat)
                        : '',
                      end ? convertSelectedDateToString(end, dateFormat) : '',
                    ])
                    pickerRef.current?.setOpen(false)
                  }}
                  size="large"
                >
                  Apply
                </Button>
              </div>
            </>
          )}
        </ReactDatePicker>
      </div>
    )
  }
}

export default styled((props: Props) => <DatePicker {...props} />)(
  ({ selectsRange }) => ({
    '&&&.mobile-date-picker': {
      position: 'absolute',
      width: '100% !important',
      overflow: 'hidden',

      '.react-datepicker-popper': {
        width: '100vw',
        marginTop: 0,
        transform: 'none !important',
        paddingTop: 0,
      },

      '.react-datepicker__month-container': {
        paddingTop: 230,
        marginLeft: 'auto',
        marginRight: 'auto',
      },

      '.react-datepicker__tab-loop': {
        '.react-datepicker': {
          borderRadius: 0,
          height: '100vh',
          boxShadow: 'none',

          '.react-datepicker__navigation--previous': {
            top: 258,
            left: 52,
          },

          '.react-datepicker__navigation--next': {
            top: 258,
            right: 57,
          },

          '.left-panel': {
            borderRadius: 0,
            flexDirection: 'column',

            '& > div:first-of-type': {
              display: 'flex',
              justifyContent: 'flex-end',
            },
          },

          '.datepicker-ctas': {
            width: '87%',
            flexDirection: 'column',
            top: 600,

            'button:first-of-type': {
              order: 2,
              padding: '24px 0',
            },

            '.cancel': {
              marginRight: 0,
            },
          },
        },
      },
    },

    '&&&.tablet-date-picker': {
      '.react-datepicker__month-container': {
        paddingTop: selectsRange ? 140 : 0,
      },

      '.react-datepicker__tab-loop': {
        '.react-datepicker': {
          '.react-datepicker__navigation--previous': {
            top: selectsRange ? 167 : undefined,
            left: 22,
          },

          '.react-datepicker__navigation--next': {
            top: selectsRange ? 167 : undefined,
            right: 32,
          },
        },
      },
    },

    '&&&.tablet-date-picker, &&&.mobile-date-picker': {
      '.react-datepicker__tab-loop': {
        '.react-datepicker': {
          ...(selectsRange && {
            paddingLeft: 0,
          }),

          '.left-panel': {
            height: 'auto',
            width: '100%',
            display: 'flex',
            gap: 8,
          },

          '.left-panel>div': {
            width: '100%',
          },
        },
      },
    },

    '&&&:not(.mobile-date-picker)': {
      position: 'relative',
    },

    '&&&': {
      '.react-datepicker-wrapper': {
        position: 'absolute',
        top: 0,
        left: 0,
        opacity: 0,
        height: '100%',
        width: '100%',
        zIndex: -2,

        '.react-datepicker__input-container': {
          height: '100%',

          input: {
            height: '100%',
            width: '100%',
          },
        },
      },

      // native react-datepicker classes
      '.react-datepicker__children-container': {
        // Items set via absolute positioning but want to make this not take up space
        width: 0,
        padding: 0,
        margin: 0,
      },

      '.react-datepicker__day-names': {
        marginTop: 20,
      },

      // We don't want to show the datepicker triangle
      '.react-datepicker__triangle': {
        display: 'none',
      },

      '.react-datepicker-popper': {
        zIndex: 3,
      },

      '.react-datepicker__tab-loop': {
        div: {
          ...Fonts.bodyMd,
          color: Colors.black,
          fontWeight: FontWeight.MEDIUM,
        },

        '.react-datepicker': {
          border: 'none',
          borderRadius: 8,
          display: 'flex',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',

          ...(selectsRange && {
            paddingLeft: 240,
            paddingBottom: 120,
          }),

          '.react-datepicker__header': {
            backgroundColor: Colors.white,
            border: 'none',
            marginTop: 20,
          },

          '.react-datepicker__current-month': {
            ...Fonts.h2,
            marginBottom: 24,
          },

          '.react-datepicker__month': {
            margin: '0 20px 10px',
          },

          '.react-datepicker__day': {
            width: 45,
            height: 34,
            margin: '2px 0',
            paddingTop: 5,

            '&:empty': {
              height: 0,
              padding: 0,
            },
          },

          '.react-datepicker__day--outside-month': {
            color: Colors.mediumGray,
          },

          '.react-datepicker__day-name': {
            ...Fonts.eyebrow,
            color: Colors.mediumGray,
            width: 35,
            margin: '0 5px',
          },

          '.react-datepicker__day--keyboard-selected': {
            backgroundColor: Colors.white,
          },

          '.react-datepicker__navigation--previous': {
            top: 25,
            left: selectsRange ? 265 : 22,
          },

          '.react-datepicker__navigation--next': {
            top: 25,
            right: 32,
          },

          '.react-datepicker__navigation-icon--previous::before,.react-datepicker__navigation-icon--next::before':
            {
              borderColor: Colors.black,
              height: 8,
              width: 8,
              borderTopWidth: 1,
              borderRightWidth: 1,
            },

          '.react-datepicker__day--today': {
            boxShadow: `0px 0px 0px 1px ${Colors.green} inset`,
            borderRadius: 8,
            color: Colors.green,
          },

          '.react-datepicker__day--in-selecting-range, .react-datepicker__day--in-range':
            {
              backgroundColor: Colors.moss,
              borderRadius: 0,
            },

          '.react-datepicker__day--disabled': {
            backgroundColor: Colors.gray,
            color: Colors.black,
          },

          // This needs to be below "today" so it takes precedence
          '.react-datepicker__day--selected, .react-datepicker__day--selecting-range-start, .react-datepicker__day--selecting-range-end, .react-datepicker__day--range-start, .react-datepicker__day--range-end':
            {
              backgroundColor: Colors.green,
              color: Colors.white,
            },

          '.react-datepicker__day--selecting-range-start, .react-datepicker__day--range-start':
            {
              borderRadius: '8px 0 0 8px',
            },

          '.react-datepicker__day--range-end': {
            borderRadius: '0 8px 8px 0',
          },

          '.react-datepicker__day--selected, .react-datepicker__day--range-end.react-datepicker__day--range-start':
            {
              borderRadius: 8,
            },

          '.left-panel': {
            backgroundColor: Colors.stone,
            borderBottomLeftRadius: 8,
            borderTopLeftRadius: 8,
            height: '100%',
            left: 0,
            padding: 24,
            position: 'absolute',
            width: 240,
          },

          '.datepicker-ctas': {
            display: 'flex',
            position: 'absolute',
            right: 24,
            bottom: 24,

            '.cancel': {
              marginRight: 40,
            },
          },
        },
      },

      // For year picker
      '.react-datepicker__year--container': {
        width: 355,
      },

      '.react-datepicker__year-wrapper': {
        maxWidth: 'initial',
      },

      '.react-datepicker__year-text': {
        border: `solid 1px ${Colors.darkGray}`,
        borderRadius: 8,
        padding: '10px 11px',
        width: 100,
        marginLeft: 10,
        marginBottom: 6,

        '&.react-datepicker__year-text--selected': {
          borderColor: Colors.transparent,
          backgroundColor: Colors.green,
          color: Colors.white,
        },

        '&.react-datepicker__year-text--disabled': {
          backgroundColor: Colors.gray,
          color: Colors.black,
        },
      },
    },

    '.dateInput .icon.input i.close.icon.icon-clear': {
      top: '0 !important',
    },
  })
)

export type { Props as DatePickerProps }
